import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { FileTextIcon, MessageCircleIcon, XIcon } from 'lucide-react';
import { EMAIL_RE } from '../utils/leadStore';
interface WhatsAppGateModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (email: string) => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function WhatsAppGateModal({
  open,
  onClose,
  onConfirm
}: WhatsAppGateModalProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (!open) return;
    setError('');
    const t = window.setTimeout(() => inputRef.current?.focus(), 60);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value)) {
      setError('Escribe un correo válido para enviarte tu cotización.');
      return;
    }
    onConfirm(value);
  };
  return <AnimatePresence data-id="element-347" data-mp="1omnarx3d3r0z">
      {open && <motion.div className="fixed inset-0 z-[60] flex items-end justify-center bg-navy-900/70 p-0 sm:items-center sm:p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} onClick={onClose} data-id="element-348" data-mp="1oprlldbc1l0m">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="wa-gate-title" onClick={e => e.stopPropagation()} initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 16,
        scale: 0.98
      }} transition={{
        duration: 0.24,
        ease: EASE
      }} className="relative w-full max-w-md rounded-t-2xl bg-white p-5 text-navy-900 shadow-2xl sm:rounded-2xl sm:p-7" style={{
        paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))'
      }} data-id="element-349" data-mp="3aluwa2e0i6h">
            <button type="button" onClick={onClose} aria-label="Cerrar" className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-md text-steel-600 transition-colors duration-150 ease-out hover:bg-steel-100 hover:text-navy-900" data-id="element-350" data-mp="1uglxqoye31me">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-351" data-mp="2d96mpvaywmkx" />
            </button>

            <h2 id="wa-gate-title" className="pr-8 font-display text-2xl font-bold leading-tight" data-id="element-352" data-mp="2d73r8vr1q18t">
              Último paso antes de WhatsApp
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-steel-600" data-id="element-353" data-mp="2rwojm7iagcws">
              ¿A qué correo te mandamos tu cotización formal y la lista de precios de fábrica 2026?
            </p>

            <div className="mt-4 flex items-start gap-2.5 rounded-lg bg-steel-50 px-3.5 py-3 text-sm text-navy-800" data-id="element-354" data-mp="2hx61lwlxv17d">
              <FileTextIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent-500" aria-hidden="true" data-id="element-355" data-mp="2mvhe6f3mbpta" />
              Así tienes tu precio por escrito y no se pierde entre los mensajes de la obra.
            </div>

            <form onSubmit={submit} noValidate className="mt-4" data-id="element-356" data-mp="2dygqybsabxrf">
              <label htmlFor="wa-gate-email" className="block text-sm font-semibold text-navy-800" data-id="element-357" data-mp="1z8j1lsimd20n">
                Tu email
              </label>
              <input ref={inputRef} id="wa-gate-email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" placeholder="tu@empresa.com" value={email} onChange={e => {
            setEmail(e.target.value);
            if (error) setError('');
          }} aria-invalid={Boolean(error)} aria-describedby="wa-gate-hint" className={`mt-1.5 block h-12 w-full min-w-0 appearance-none rounded-md border-2 bg-white px-3.5 text-navy-900 placeholder:text-steel-400 focus:outline-none ${error ? 'border-red-500' : 'border-steel-200 focus:border-accent-500'}`} data-id="element-358" data-mp="2447z7qb2w8ub" />
              <p id="wa-gate-hint" className={`mt-1.5 text-xs ${error ? 'text-red-600' : 'text-steel-600'}`} data-id="element-359" data-mp="ufpugcmmku7n">
                {error || 'Sin spam. Solo tu cotización y seguimiento de tu pedido.'}
              </p>

              <button type="submit" className="mt-4 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-md bg-accent-500 px-4 text-base font-semibold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-accent-600 active:scale-[0.99] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500" data-id="element-360" data-mp="2othd21mxmof8">
                <MessageCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-361" data-mp="316us8asywp3s" />
                <span className="whitespace-nowrap" data-id="element-362" data-mp="36333hd41ny5o">Continuar a WhatsApp</span>
              </button>
              <p className="mt-2.5 text-center text-xs text-steel-600" data-id="element-363" data-mp="29fewnr9fns1f">
                Al continuar aceptas recibir seguimiento por WhatsApp y email.
              </p>
            </form>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}